import pixRegexUtils from '@/services/regularExpressions/pixRegex.utils.js';
import computedOperationUtils from '@/services/common/computedOperation.utils';
import regexConst from '@/consts/regex.const.js';
import confidentialityConst from '@/consts/confidentiality.const';
import store from '@/store';
import genericTabConst from '@/consts/genericTab.const.js';

const emptyGUID = '00000000-0000-0000-0000-000000000000';

export default {
  getFieldValueFromComp(comparable, fieldName, currentLang) {
    let result;
    const wordMatches = fieldName.split('.');

    // If the metricKey is something like ParentInfo.metricKey, go look for the value inside the parent of this comparable
    if (wordMatches[0] === 'ParentInfo') {
      return this.getFieldValueFromComp(comparable.ParentInfo || comparable, wordMatches[1], currentLang?.toUpperCase());
    }

    if (comparable) {
      if (comparable[fieldName] !== undefined) {
        result = comparable[fieldName];
      } else {
        const fieldFeatures = this.getFieldValueInFeatures(comparable, fieldName, currentLang);
        result = this.isNumericType(fieldFeatures?.FeatureDataType) && ![null, undefined].includes(fieldFeatures?.Value) ? Number(fieldFeatures?.Value) : fieldFeatures?.Value;
      }
    }

    if (!result && comparable?.ComparableTypeID === 3) {
      result = this.getFieldValueFromCompIe(comparable, fieldName, currentLang);
    }

    return result ?? null;
  },

  isNumericType(value) {
    return ['integer', 'int', 'double', 'decimal'].includes(value);
  },

  incomeExpenses12Month(comparable, currentLang) {
    const savedBasketCompWithIncomeExpenses12Month = comparable?.Features ? this.getFieldValueInFeatures(comparable, 'SavedBasketCompWithIncomeExpenses12Month', currentLang) : null;
    if (!savedBasketCompWithIncomeExpenses12Month) {
      return store.getters['comparablesModule/getLastSelectedTwelveMonthFlagFilter'](genericTabConst.IE_COMPS);
    }
    return savedBasketCompWithIncomeExpenses12Month.Value === 'true';
  },

  getFieldValueFromCompIe(comparable, fieldName, currentLang) {
    const wordMatches = fieldName.split('.');
    const ieTwelveMonthFlag = this.incomeExpenses12Month(comparable, currentLang);

    if (wordMatches[0] === 'IncomeExpenses') {
      if (comparable.IncomeExpenses && !ieTwelveMonthFlag) {
        return comparable.IncomeExpenses[wordMatches[1]];
      } else if (comparable.IncomeExpenses12Month && ieTwelveMonthFlag) {
        return comparable.IncomeExpenses12Month[wordMatches[1]];
      }
    }
    if (comparable) {
      const fieldValueFromComp = comparable.Features[currentLang?.toUpperCase()]?.find(({ InternalName }) => InternalName === fieldName)?.Value;
      return ieTwelveMonthFlag
        ? comparable[fieldName] || comparable?.IncomeExpenses12Month?.[fieldName] || this.getFieldValueInMeasures(comparable, fieldName, ieTwelveMonthFlag)?.Value || fieldValueFromComp
        : comparable[fieldName] || comparable?.IncomeExpenses?.[fieldName] || this.getFieldValueInMeasures(comparable, fieldName)?.Value || fieldValueFromComp;
    } else {
      return null;
    }
  },

  getVisibleImages(comparable) {
    return comparable?.Documents?.filter(({ FileType, PhotoTypeParam, ContentType }) => (FileType === 601 && (PhotoTypeParam === '04' || PhotoTypeParam === '14')) || ContentType === 'Image Gallery');
  },

  getFieldValueInFeatures({ Features, DefaultLanguage }, fieldToLookFor, currentLang) {
    return (
      Features &&
      (Features[currentLang]?.find((a) => a.InternalName === fieldToLookFor) || // found in current lang
        Features[DefaultLanguage || 'EN']?.find((a) => a.InternalName === fieldToLookFor)) // found in default lang
    );
  },

  getFieldValueInMeasures(comparable, fieldToLookFor, ieTwelveMonthFlag) {
    return ieTwelveMonthFlag
      ? comparable.IncomeExpenses12Month && comparable.IncomeExpenses12Month.Measures.find((a) => a.Type === fieldToLookFor)
      : comparable.IncomeExpenses && comparable.IncomeExpenses.Measures.find((a) => a.Type === fieldToLookFor);
  },

  setFieldValueInComparable(comparable, { InternalField, Label, FieldDataType, Value }, idF = 3000) {
    /**Added this condition because in object of comps does't exist the internalField with this prefix */
    const internalFieldWithoutPrefix = InternalField.includes('ParentInfo.') ? InternalField.split('.').pop() : InternalField;

    if (Object.prototype.hasOwnProperty.call(comparable, internalFieldWithoutPrefix)) {
      comparable[internalFieldWithoutPrefix] = Value;
    } else {
      this.setFieldValueInFeatures(comparable, internalFieldWithoutPrefix, Value, Label, FieldDataType, idF);
    }
  },

  setFieldValueInFeatures(comparable, fieldToLookFor, value, label, dataType, IdF = 3000) {
    /*
          0 < idF < 1000 -> comes from datahub
          1000 < idF < 2000 -> comes from profile form (edited comparable)
          2000 < idF < 3000 -> comes from new comparable
          idF === 3000 -> comes from this function's default value, not set/mapped manually
      */

    const mapDataType = function (inDataType) {
      const dataTypeMapping = {
        bool: 'boolean',
        datetime: 'date',
        decimal: 'decimal',
        int: 'integer',
        string: 'text',
      };

      const outDataType = dataTypeMapping[inDataType] || inDataType;
      return outDataType;
    };

    dataType = mapDataType(dataType);

    const found = this.getFieldValueInFeatures(comparable, fieldToLookFor);
    if (found) {
      found.Value = value;
      found.FeatureDataType = dataType; // Para sobreescribir aquellas Features que no tuvieran dataType en su momento
      found.IdF = IdF; // Para sobreescribir aquellas Features que no tuvieran idF en su momento
    } else {
      if (!comparable.Features) {
        comparable.Features = {};
      }
      if (!comparable.DefaultLanguage) {
        comparable.DefaultLanguage = 'EN';
      }
      const defaultFeatures = comparable.Features[comparable.DefaultLanguage] || [];
      const newFeature = {
        IdF,
        InternalName: fieldToLookFor,
        Label: label || fieldToLookFor,
        Value: value?.toString() ?? null,
        Metric: null,
        Frequency: null,
        Description: null,
        FeatureDataType: dataType,
      };
      comparable.Features[comparable.DefaultLanguage] = defaultFeatures.concat(newFeature);
    }
  },

  isEmptyGuid(guid) {
    return !guid || guid === emptyGUID;
  },

  // TODO refactor this method with isRelative when the time is right
  findDuplicatedComparablesFrom(array, comparable) {
    // find same Id Indexes in case there are literal duplicates
    const duplicatedCompsIndexes = new Set();
    duplicatedCompsIndexes.add(array.findIndex((comp) => comp.id === comparable.id));
    // if there is not an originalId not a SourceId (we just created the copy only in frontend) then this comps id is the original id (it is not a copy)
    const maybeSourceId = this.isEmptyGuid(comparable.SourceId) ? undefined : comparable.SourceId;
    const maybeOriginalSourceId = this.isEmptyGuid(comparable.OriginalSourceId) ? undefined : comparable.OriginalSourceId;
    const originalId = maybeOriginalSourceId || maybeSourceId;
    if (originalId) {
      const originalCompIndex = array.findIndex((comp) => comp.id === originalId);
      const otherDuplicatesIndexes = array.map((comp, index) => {
        return comp.OriginalSourceId === originalId ? index : -1;
      });
      duplicatedCompsIndexes.add(originalCompIndex);
      otherDuplicatesIndexes.forEach((i) => duplicatedCompsIndexes.add(i));
    }
    // filter not found values
    duplicatedCompsIndexes.delete(-1);
    return [...duplicatedCompsIndexes];
  },

  applyTransform(saveTransform, value, metricKey) {
    // TODO: Standarize the way a transformation value is calculated
    if (saveTransform && value) {
      const computedOperation = computedOperationUtils.getReplacedHardcodedComputedOperationStrings(saveTransform);
      const regexMatches = pixRegexUtils.getRegexMatches(regexConst.WHOLE_WORD, computedOperation);

      let replacedInternalFields = computedOperation;

      regexMatches.forEach(function (internalField) {
        if (internalField !== 'null') {
          if (value !== null && value !== undefined && value !== '') {
            replacedInternalFields = replacedInternalFields.replace(new RegExp(String.raw`${internalField}\b`, 'g'), value);
          } else {
            replacedInternalFields = replacedInternalFields.replace(new RegExp(String.raw`${internalField}\b`, 'g'), 'null');
          }
        }
      });

      const finalValue = computedOperationUtils.mathEvaluation(replacedInternalFields, metricKey);
      return finalValue;
    }

    if (value === 'true') {
      return true;
    }

    if (value === 'false') {
      return false;
    }

    if (value === 'null') {
      return null;
    }

    return value;
  },

  sortBasketComparables(comp1, comp2) {
    if (comp1.ComparableCode < comp2.ComparableCode || comp2.ComparableCode === null) {
      return -1;
    }
    if (comp1.ComparableCode > comp2.ComparableCode || comp1.ComparableCode === null) {
      return 1;
    }
    return 0;
  },

  isRelative({ maybeOriginal, maybeDerived }) {
    return (
      maybeOriginal.id === maybeDerived.SourceId || // Comp from original
      maybeOriginal.id === maybeDerived.OriginalSourceId || // Comp from original
      (maybeOriginal.SourceId && maybeOriginal.SourceId === maybeDerived.SourceId) || // Comp from VAS_JLL
      (maybeOriginal.OriginalSourceId && maybeOriginal.OriginalSourceId === maybeDerived.SourceId) || // Comp from VAS_JLL
      (maybeOriginal.SourceId && maybeOriginal.SourceId === maybeDerived.OriginalSourceId) || // Comp from VAS_JLL copy
      (maybeOriginal.OriginalSourceId && maybeOriginal.OriginalSourceId === maybeDerived.OriginalSourceId)
    ); // Comp from VAS_JLL copy
  },

  shouldBlurConfidentialFields(comparable) {
    const confidentialUserIsNotLoggedUser = comparable?.ConfidentialBy?.toLowerCase() !== store.getters['authModule/UserName']?.toLowerCase();
    const comparableIsConfidential = !!comparable?.IsConfidential;
    const hasHighConfidentiality = comparable?.LevelOfConfidentiality === confidentialityConst.HIGH;

    return confidentialUserIsNotLoggedUser && comparableIsConfidential && hasHighConfidentiality;
  },

  isConfidential(comparable) {
    return !!comparable?.IsConfidential;
  },

  shouldMarkConfidentialFieldsAsReadonly(comparable) {
    const confidentialUserIsNotLoggedUser = comparable?.ConfidentialBy?.toLowerCase() !== store.getters['authModule/UserName']?.toLowerCase();
    const hasMediumConfidentiality = comparable?.LevelOfConfidentiality === confidentialityConst.MEDIUM;
    const comparableIsConfidential = !!comparable?.IsConfidential;

    return confidentialUserIsNotLoggedUser && comparableIsConfidential && hasMediumConfidentiality;
  },

  shouldMarkConfidentialityFieldsAsReadonly(comparable) {
    const confidentialUserIsNotLoggedUser = comparable?.ConfidentialBy?.toLowerCase() !== store.getters['authModule/UserName']?.toLowerCase();
    const hasMediumConfidentiality = comparable?.LevelOfConfidentiality === confidentialityConst.MEDIUM;
    const hasHighConfidentiality = comparable?.LevelOfConfidentiality === confidentialityConst.HIGH;
    const comparableIsConfidential = !!comparable?.IsConfidential;

    return confidentialUserIsNotLoggedUser && comparableIsConfidential && (hasMediumConfidentiality || hasHighConfidentiality);
  },

  mapFeaturesToGenericField(comparable, currentLang = 'EN') {
    const features = comparable?.Features?.[currentLang.toUpperCase()] ?? [];
    const featuresAsGenericFields = features.map(({ InternalName: InternalField, Value }) => ({
      InternalField,
      Value,
    }));

    return featuresAsGenericFields;
  },

  mapComparableToGenericFields(comparable, currentLang = 'EN') {
    return Object.keys(comparable).reduce((acc, comparableProperty) => {
      if (comparableProperty === 'Features') {
        return [...acc, ...this.mapFeaturesToGenericField(comparable, currentLang)];
      }
      return [
        ...acc,
        {
          InternalField: comparableProperty,
          Value: comparable[comparableProperty],
        },
      ];
    }, []);
  },
};
